import {
  listPlanFUploadsEndPoint,
  planFCsvUrlEndPoint,
  uploadPlanFEndPoint,
} from '../../common/api.endPoints';
import { HttpService } from '../../common/http.service';

export interface PlanFUploadHistoryRow {
  historyId: number;
  originalFilename: string;
  carrier?: string;
  totalRows: number;
  recognizedPolicyCount: number;
  flaggedRowCount: number;
  autoIgnoredRowCount: number;
  duplicateSkippedCount: number;
  missingApplicationCount: number;
  taskCreatedCount: number;
  noteCreatedCount: number;
  uploadedBy?: string;
  uploadedAt?: string;
}

// Returned by uploadPlanFCsv — extends the history row with row-level arrays for the
// immediate post-upload UI. These arrays are NOT returned by listPlanFUploads.
export interface PlanFUploadResult extends PlanFUploadHistoryRow {
  processedRows?: unknown[];
  skippedRows: unknown[];
  failedRows: unknown[];
}

export interface PlanFCsvUrlResponse {
  url: string;
  originalFilename?: string;
}

export const uploadPlanFCsv = async (
  file: File,
): Promise<PlanFUploadResult> => {
  const formData = new FormData();
  formData.append('file', file);
  // postFiles resolves with the parsed JSON body and rejects on HTTP errors,
  // so a try/catch at the call site is enough — no error wrapping to unpack.
  const data = await HttpService.getInstance().postFiles(
    uploadPlanFEndPoint(),
    formData,
  );
  // Double-cast: postFiles returns the loose IKeyValMap; the backend contract
  // for this endpoint is PlanFUploadResult.
  return data as unknown as PlanFUploadResult;
};

export const listPlanFUploads = async (): Promise<PlanFUploadHistoryRow[]> => {
  const response = await HttpService.getInstance().get(
    listPlanFUploadsEndPoint(),
  );
  if (response?.error) {
    throw response.error;
  }
  const data = response?.parsedBody ?? response;
  if (!Array.isArray(data)) {
    throw new Error('Unexpected Plan F upload history response');
  }
  return data;
};

export const getPlanFCsvUrl = async (
  historyId: number,
  download = false,
): Promise<PlanFCsvUrlResponse> => {
  const response = await HttpService.getInstance().get(
    planFCsvUrlEndPoint(historyId, download),
  );
  if (response?.error) {
    throw response.error;
  }
  const data = response?.parsedBody ?? response;
  if (!data?.url) {
    throw new Error('Unexpected Plan F CSV URL response');
  }
  return data as PlanFCsvUrlResponse;
};
